/**
 * useModalDragResize
 *
 * Shared composable for modal drag and resize functionality.
 * Used by StatsModal, SettingsModal, ProjectModal, RecruitmentModal.
 */

import { ref, computed, onUnmounted } from 'vue';

export interface ModalDragResizeOptions {
  initialX?: number;
  initialY?: number;
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  minHeight?: number;
  zIndexBase?: number;
}

export function useModalDragResize(options: ModalDragResizeOptions) {
  const {
    initialX = 100,
    initialY = 100,
    initialWidth,
    initialHeight,
    minWidth = 200,
    minHeight = 150,
    zIndexBase = 1000
  } = options;

  // Position and size
  const x = ref(initialX);
  const y = ref(initialY);
  const width = ref(initialWidth);
  const height = ref(initialHeight);
  const zIndex = ref(zIndexBase);

  // Flash animation
  const isFlashing = ref(false);
  let flashTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Dragging state
  const isDragging = ref(false);
  const dragStartX = ref(0);
  const dragStartY = ref(0);
  const dragOffsetX = ref(0);
  const dragOffsetY = ref(0);

  // Resizing state
  const isResizing = ref(false);
  const resizeMode = ref<'right' | 'bottom' | 'corner' | null>(null);
  const resizeStartX = ref(0);
  const resizeStartY = ref(0);
  const resizeStartWidth = ref(0);
  const resizeStartHeight = ref(0);

  // Computed style for modal positioning
  const modalStyle = computed(() => ({
    left: `${x.value}px`,
    top: `${y.value}px`,
    width: `${width.value}px`,
    height: `${height.value}px`,
    zIndex: zIndex.value
  }));

  /**
   * Trigger flash animation on header
   */
  const flashHeader = () => {
    // Clear any existing timeout to prevent stacking
    if (flashTimeoutId) {
      clearTimeout(flashTimeoutId);
    }
    isFlashing.value = true;
    flashTimeoutId = setTimeout(() => {
      isFlashing.value = false;
      flashTimeoutId = null;
    }, 500);
  };

  /**
   * Set z-index externally
   */
  const setZIndex = (newZIndex: number) => {
    zIndex.value = newZIndex;
  };

  /**
   * Start dragging
   */
  const startDrag = (e: MouseEvent, headerRef: HTMLElement | undefined) => {
    if (!headerRef) return;

    if (e.target !== headerRef && !headerRef.contains(e.target as Node)) {
      return;
    }

    if ((e.target as HTMLElement).classList.contains('close-btn')) {
      return;
    }

    isDragging.value = true;
    dragStartX.value = e.clientX;
    dragStartY.value = e.clientY;
    dragOffsetX.value = x.value;
    dragOffsetY.value = y.value;

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', stopDrag);

    e.preventDefault();
  };

  /**
   * Handle drag movement
   */
  const handleDrag = (e: MouseEvent) => {
    if (!isDragging.value) return;

    const deltaX = e.clientX - dragStartX.value;
    const deltaY = e.clientY - dragStartY.value;

    let newX = dragOffsetX.value + deltaX;
    let newY = dragOffsetY.value + deltaY;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    newX = Math.max(0, Math.min(newX, viewportWidth - width.value));
    newY = Math.max(0, Math.min(newY, viewportHeight - 50));

    x.value = newX;
    y.value = newY;
  };

  /**
   * Stop dragging
   */
  const stopDrag = () => {
    isDragging.value = false;
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', stopDrag);
  };

  /**
   * Start resizing
   */
  const startResize = (mode: 'right' | 'bottom' | 'corner', e: MouseEvent) => {
    isResizing.value = true;
    resizeMode.value = mode;
    resizeStartX.value = e.clientX;
    resizeStartY.value = e.clientY;
    resizeStartWidth.value = width.value;
    resizeStartHeight.value = height.value;

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);

    e.preventDefault();
  };

  /**
   * Handle resize movement
   */
  const handleResize = (e: MouseEvent) => {
    if (!isResizing.value || !resizeMode.value) return;

    const deltaX = e.clientX - resizeStartX.value;
    const deltaY = e.clientY - resizeStartY.value;

    if (resizeMode.value === 'right' || resizeMode.value === 'corner') {
      width.value = Math.max(minWidth, resizeStartWidth.value + deltaX);
    }

    if (resizeMode.value === 'bottom' || resizeMode.value === 'corner') {
      height.value = Math.max(minHeight, resizeStartHeight.value + deltaY);
    }
  };

  /**
   * Stop resizing
   */
  const stopResize = () => {
    isResizing.value = false;
    resizeMode.value = null;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
  };

  /**
   * Center modal in viewport
   */
  const centerInViewport = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    x.value = Math.max(20, (viewportWidth - width.value) / 2);
    y.value = Math.max(70, (viewportHeight - height.value) / 2);
  };

  /**
   * Position modal near a point (e.g., mouse position)
   */
  const positionNear = (mouseX: number, mouseY: number) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let modalX = mouseX + 20;
    if (modalX + width.value > viewportWidth - 20) {
      modalX = mouseX - width.value - 20;
    }
    modalX = Math.max(20, modalX);

    let modalY = mouseY - 20;
    if (modalY + height.value > viewportHeight - 20) {
      modalY = viewportHeight - height.value - 20;
    }
    modalY = Math.max(20, modalY);

    x.value = modalX;
    y.value = modalY;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', stopDrag);
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
    // Clear flash timeout
    if (flashTimeoutId) {
      clearTimeout(flashTimeoutId);
    }
  });

  return {
    // Position and size
    x,
    y,
    width,
    height,
    zIndex,
    modalStyle,

    // Flash
    isFlashing,
    flashHeader,

    // Z-index
    setZIndex,

    // Drag
    startDrag,

    // Resize
    startResize,

    // Positioning helpers
    centerInViewport,
    positionNear
  };
}
